import React, { useEffect, useMemo, useState } from 'react';
import { Card, Box, Typography, Divider, Skeleton, MenuItem, Select } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getDialyRevenueData } from '../../../../../redux/features/poinTrackerSlice';

export default function DailyRevenueTableCard({ fromDate, toDate, selectedTeam }) {
  const [limit, setLimit] = useState(10);
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.dailyRevenueData);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getDialyRevenueData(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const revenueData = useMemo(() => {
    const data = responseData?.data || [];
    return data.map((item, index) => ({
      date: item.date,
      revenue: item.revenue,
      sales: item.sales,
      admissions: item.admissions,
    }));
  }, [responseData]);

  const displayedData = useMemo(() => {
    return [...revenueData].reverse().slice(0, limit);
  }, [revenueData, limit]);
  return (
    <Card sx={{ p: 3, borderRadius: 4, boxShadow: 3, width: '100%', bgcolor: '#fff', mt: 5 }}>
      {/* Card Title */}
      {/* Title */}
      {loading ? (
        <Skeleton width="40%" height={25} animation="wave" sx={{ mb: 3 }} />
      ) : (
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          Daily Revenue
        </Typography>
      )}

      {/* Header Labels */}
      <Box sx={{ display: 'flex', px: 2, mb: 1 }}>
        {loading ? (
          <>
            <Skeleton width="25%" />
            <Skeleton width="20%" sx={{ mx: 'auto' }} />
            <Skeleton width="15%" sx={{ mx: 'auto' }} />
            <Skeleton width="20%" sx={{ ml: 'auto' }} />
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ flex: 1.2, fontWeight: 'bold', color: 'text.secondary' }}>
              Date
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', textAlign: 'center' }}
            >
              Revenue
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 0.8, fontWeight: 'bold', color: 'text.secondary', textAlign: 'center' }}
            >
              Sales
            </Typography>
            <Typography
              variant="caption"
              sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary', textAlign: 'right' }}
            >
              Admissions
            </Typography>
          </>
        )}
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      {/* Table Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {loading
          ? [...Array(6)].map((_, index) => (
              <Box key={index}>
                <Box sx={{ display: 'flex', p: 1.8, px: 2 }}>
                  <Skeleton sx={{ flex: 1.2 }} height={20} />
                  <Skeleton sx={{ flex: 1 }} height={20} />
                  <Skeleton sx={{ flex: 0.8 }} height={20} />
                  <Skeleton sx={{ flex: 1 }} height={20} />
                </Box>
                {index !== 5 && <Divider sx={{ mx: 2 }} />}
              </Box>
            ))
          : displayedData.map((row, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.8,
                    px: 2,
                    borderRadius: row.isSelected ? 2 : 0,
                    // Highlight logic
                    bgcolor: row.isSelected ? '#f0f7ff' : 'transparent',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: row.isSelected ? '#e0efff' : '#f8fafc' },
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1.2, color: '#64748b', fontWeight: 500 }}>
                    {row.date}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ flex: 1, color: '#10b981', fontWeight: 'bold', textAlign: 'center' }}
                  >
                    {row.revenue}
                  </Typography>

                  <Typography variant="body2" sx={{ flex: 0.8, color: '#475569', textAlign: 'center' }}>
                    {row.sales}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ flex: 1, color: '#2563eb', fontWeight: 'bold', textAlign: 'right' }}
                  >
                    {row.admissions}
                  </Typography>
                </Box>

                {/* Show divider only if NOT selected and NOT the last item */}
                {!row.isSelected && index !== displayedData.length - 1 && <Divider sx={{ mx: 2, opacity: 0.5 }} />}
              </Box>
            ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Select
          size="small"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          sx={{
            height: 32,
            fontSize: 12,
            '& .MuiSelect-select': { py: 0.5, px: 1 },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>

        <Typography variant="caption" fontSize={12}>
          {`${limit} / ${revenueData.length}`}
        </Typography>
      </Box>
    </Card>
  );
}
